import { Auth, User, getAuth } from "firebase/auth";

import { FirebaseApp, initializeApp } from "firebase/app";
import {
  DocumentReference,
  FieldValue,
  Firestore,
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { GameState } from "./store/gameStateSlice";
import { PlayersDataPayload, PlayerData } from "./store/playersSlice";
import { performInitialRolls, rollDiceImpl } from "./store/dice";
import { DiceData } from "./store/diceSlice";
import { GameBoardState, GameMode, Player } from "./Types";
import { LONG_NARDY_BOARD_STATE, STARTING_BOARD_STATE } from "./Constants";
import { NetworkedMovesPayload } from "./store/animatableMovesSlice";
import {
  InitialReadyForNextGameData,
  ReadyForNextGameData,
} from "./store/readyForNextGameSlice";
import {
  DoublingCubeData,
  InitialDoublingCubeState,
} from "./store/doublingCubeSlice";
import { InitialMatchScoreState, MatchScore } from "./store/matchScoreSlice";

const FIREBASE_CONFIG = {
  apiKey: "AIzaSyCmS7jIh9e_lGLpRPwZpWY0kIqiLjKxn54",
  authDomain: "backgammon-574d7-d207a.firebaseapp.com",
  projectId: "backgammon-574d7-d207a",
  storageBucket: "backgammon-574d7-d207a.firebasestorage.app",
  messagingSenderId: "10285367249",
  appId: "1:10285367249:web:70c7dc1a0d30857fe16af0",
  measurementId: "G-KN2DN56831",
};

// Инициализируем сразу при загрузке модуля — никаких проблем с порядком вызовов
const app: FirebaseApp = initializeApp(FIREBASE_CONFIG);
const auth: Auth = getAuth(app);
const db: Firestore = getFirestore(app);

function initialize() {
  // Оставляем для обратной совместимости, Firebase уже инициализирован выше
}

export function createRoomCode(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  var result = "";
  for (var i = 0; i < 4; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export function getDB(): Firestore {
  initialize();
  return db;
}

export function getFirebaseAuth(): Auth {
  initialize();
  return auth;
}

export function signIn(): User | null {
  initialize();
  return auth.currentUser;
}

export function getCurrentUser(): User {
  initialize();

  if (auth.currentUser != null) {
    return auth.currentUser;
  }

  throw Error("Trying to access current user without signing in.");
}

export type FirestoreGameData = {
  gameBoard: GameBoardState;
  gameState: GameState;
  players: PlayersDataPayload;
  currentPlayer: Player;
  dice: DiceData;
  networkedMoves: NetworkedMovesPayload | null;
  roomCode: string;
  doublingCube: DoublingCubeData;
  matchScore: MatchScore;
  readyForNextGame: ReadyForNextGameData;
  timeCreated: FieldValue;
  betAmount?: number;
  gameMode?: GameMode;  // undefined = Backgammon (обратная совместимость)
};

type CreateLobbyResult = {
  roomCode: string;
  docRef: DocumentReference;
};

export async function createLobby(
  hostPlayer: PlayerData,
  betAmount: number = 0,
  gameMode: GameMode = GameMode.Backgammon
): Promise<CreateLobbyResult> {
  initialize();

  const roomCode = createRoomCode();
  const initialRolls = performInitialRolls();
  const startingRoll = initialRolls[Object.keys(initialRolls).length - 1];

  const startingBoard =
    gameMode === GameMode.LongNardy ? LONG_NARDY_BOARD_STATE : STARTING_BOARD_STATE;

  const initialGameData: FirestoreGameData = {
    roomCode: roomCode,
    gameBoard: startingBoard,
    gameState: GameState.WaitingForPlayers,
    players: {
      playerOne: hostPlayer,
      playerTwo: null,
    },
    currentPlayer: startingRoll[0] > startingRoll[1] ? Player.One : Player.Two,
    dice: {
      initialRolls: initialRolls,
      currentRoll: startingRoll,
    },
    networkedMoves: null,
    doublingCube: InitialDoublingCubeState,
    matchScore: InitialMatchScoreState,
    readyForNextGame: InitialReadyForNextGameData,
    timeCreated: serverTimestamp(),
    betAmount,
    gameMode,
  };

  const docRef = await addDoc(collection(db, "lobbies"), initialGameData);

  return {
    roomCode,
    docRef,
  };
}

export async function findLobby(
  roomCode: string
): Promise<DocumentReference | null> {
  initialize();

  // Ждём пока Firebase Auth восстановит сессию (важно при перезагрузке страницы)
  let uid = auth.currentUser?.uid ?? null;
  if (!uid) {
    uid = await new Promise<string | null>((resolve) => {
      const unsub = auth.onAuthStateChanged((user) => {
        unsub();
        resolve(user?.uid ?? null);
      });
    });
  }

  const q = query(
    collection(db, "lobbies"),
    where("roomCode", "==", roomCode),
    limit(10)
  );

  const sixHoursAgo = Date.now() - 6 * 60 * 60 * 1000;

  try {
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return null;

    const docs = querySnapshot.docs.slice().sort((a, b) => {
      const tA = (a.data().timeCreated as Timestamp)?.toMillis?.() ?? 0;
      const tB = (b.data().timeCreated as Timestamp)?.toMillis?.() ?? 0;
      return tB - tA;
    });

    for (const docSnap of docs) {
      const data = docSnap.data() as FirestoreGameData;

      const created = (data.timeCreated as Timestamp)?.toMillis?.() ?? 0;
      if (created < sixHoursAgo) continue;

      const p1Uid = data.players?.playerOne?.uid;
      const p2    = data.players?.playerTwo;
      const p2Uid = p2?.uid;

      // Режоин: P1 или P2 могут вернуться в свою комнату
      // Новый игрок: слот P2 свободен
      if (p1Uid === uid || p2Uid === uid || p2 == null) {
        return docSnap.ref;
      }
    }
    return null;
  } catch (err) {
    console.error("findLobby query failed:", err);
    return null;
  }
}

export async function joinLobbyAsPlayerTwo(
  docRef: DocumentReference,
  playerTwo: PlayerData
) {
  return await setDoc(
    docRef,
    {
      gameState: GameState.WaitingToBegin,
      players: {
        playerTwo,
      },
    },
    { merge: true }
  );
}

export function hasJoinedLobby(playersData: PlayersDataPayload): boolean {
  let uid = getCurrentUser().uid;
  return (
    (playersData.playerOne != null && playersData.playerOne.uid === uid) ||
    (playersData.playerTwo != null && playersData.playerTwo.uid === uid)
  );
}

export async function writeNewGameStateToDB(
  docRef: DocumentReference,
  newGameState: GameState
) {
  return await setDoc(
    docRef,
    {
      gameState: newGameState,
    },
    { merge: true }
  );
}

export async function writeEndTurnToDB(
  docRef: DocumentReference,
  newGameBoardState: GameBoardState,
  newCurrentPlayer: Player,
  networkedMoves: NetworkedMovesPayload
) {
  return await setDoc(
    docRef,
    {
      gameBoard: newGameBoardState,
      currentPlayer: newCurrentPlayer,
      dice: {
        currentRoll: rollDiceImpl(),
      },
      gameState: GameState.PlayerRolling,
      networkedMoves,
    },
    { merge: true }
  );
}

export async function writeGameOverToDB(
  docRef: DocumentReference,
  newGameBoardState: GameBoardState,
  winningGameState:
    | GameState.GameOver
    | GameState.GameOverGammon
    | GameState.GameOverBackgammon
    | GameState.GameOverForfeit,
  newMatchScore: MatchScore,
  networkedMoves: NetworkedMovesPayload
) {
  return await setDoc(
    docRef,
    {
      gameBoard: newGameBoardState,
      gameState: winningGameState,
      networkedMoves,
      matchScore: newMatchScore,
    },
    { merge: true }
  );
}

export async function writeForfeitToDB(
  docRef: DocumentReference,
  newGameBoardState: GameBoardState,
  newMatchScore: MatchScore,
  forfeiter?: Player  // явный проигравший — нужен при отказе от удвоения
) {
  const data: any = {
    gameBoard: newGameBoardState,
    gameState: GameState.GameOverForfeit,
    matchScore: newMatchScore,
  };
  // Если передан forfeiter — пишем currentPlayer явно,
  // иначе оставляем как есть (обычная сдача в свой ход)
  if (forfeiter !== undefined) {
    data.currentPlayer = forfeiter;
  }
  return await setDoc(docRef, data, { merge: true });
}

export async function writeAcceptDoubleToDB(
  docRef: DocumentReference,
  newDoublingCubeData: DoublingCubeData
) {
  return await setDoc(
    docRef,
    {
      gameState: GameState.PlayerRolling,
      doublingCube: newDoublingCubeData,
    },
    { merge: true }
  );
}

export async function writeAutomaticDoubleToDB(docRef: DocumentReference) {
  return await setDoc(
    docRef,
    {
      doublingCube: {
        owner: null,
        gameStakes: 2,
      },
    },
    { merge: true }
  );
}

/** Отмечаем что игрок отключился (пишет timestamp) */
export async function writePlayerDisconnect(
  docRef: DocumentReference,
  playerSlot: "playerOne" | "playerTwo"
) {
  return setDoc(docRef, {
    disconnected: { [playerSlot]: serverTimestamp() },
  }, { merge: true });
}

/** Игрок вернулся — сбрасываем его disconnect */
export async function clearPlayerDisconnect(
  docRef: DocumentReference,
  playerSlot: "playerOne" | "playerTwo"
) {
  return setDoc(docRef, {
    disconnected: { [playerSlot]: null },
  }, { merge: true });
}

export async function writeMatchSettingsToDB(
  docRef: DocumentReference,
  pointsRequiredToWin: number,
  enableDoubling: boolean,
  gameMode: GameMode = GameMode.Backgammon,
  betAmount: number = 0
) {
  const gameBoard =
    gameMode === GameMode.LongNardy ? LONG_NARDY_BOARD_STATE : STARTING_BOARD_STATE;

  return await setDoc(
    docRef,
    {
      gameBoard,
      gameMode,
      betAmount,
      matchScore: {
        pointsRequiredToWin: pointsRequiredToWin,
      },
      doublingCube: {
        owner: null,
        gameStakes: 1,
        enabled: enableDoubling,
        playerOneHasDoubled: false,
        playerTwoHasDoubled: false,
      },
    },
    { merge: true }
  );
}

export async function writePlayerReadyForNextGameToDB(
  docRef: DocumentReference,
  newReadyForNextGameData: ReadyForNextGameData
) {
  return await setDoc(
    docRef,
    {
      readyForNextGame: newReadyForNextGameData,
    },
    { merge: true }
  );
}

export async function writeNewGameStartToDB(
  docRef: DocumentReference,
  gameMode: GameMode = GameMode.Backgammon
) {
  const initialRolls = performInitialRolls();
  const startingRoll = initialRolls[Object.keys(initialRolls).length - 1];
  const startingBoard =
    gameMode === GameMode.LongNardy ? LONG_NARDY_BOARD_STATE : STARTING_BOARD_STATE;

  return await setDoc(
    docRef,
    {
      gameBoard: startingBoard,
      gameState: GameState.WaitingToBegin,
      currentPlayer:
        startingRoll[0] > startingRoll[1] ? Player.One : Player.Two,
      dice: {
        initialRolls: initialRolls,
        currentRoll: startingRoll,
      },
      networkedMoves: null,
      doublingCube: {
        owner: null,
        gameStakes: 1,
        playerOneHasDoubled: false,
        playerTwoHasDoubled: false,
      },
      readyForNextGame: InitialReadyForNextGameData,
    },
    { merge: true }
  );
}

// ─── Public Lobby System ─────────────────────────────────────────────────────

export interface PublicLobbyData {
  id: string;
  roomCode: string;
  hostUid: string;
  hostNickname: string;
  hostRating: number;
  pointsToWin: number;
  doublingEnabled: boolean;
  betAmount: number;
  gameMode?: GameMode;
  createdAt: Timestamp;
  status: "waiting" | "in_progress";
}

const PUBLIC_LOBBIES = "publicLobbies";

export async function createPublicLobby(
  roomCode: string,
  hostNickname: string,
  hostRating: number,
  pointsToWin: number,
  doublingEnabled: boolean,
  betAmount: number,
  gameMode: GameMode = GameMode.Backgammon
): Promise<string> {
  initialize();
  const uid = getCurrentUser().uid;

  // Удаляем все старые публичные лобби этого юзера чтобы не плодить дубликаты
  try {
    const oldQ = query(
      collection(db, PUBLIC_LOBBIES),
      where("hostUid", "==", uid),
      limit(10)
    );
    const oldSnap = await getDocs(oldQ);
    await Promise.all(oldSnap.docs.map((d) => deleteDoc(d.ref)));
  } catch (_) {}

  const lobbyData = {
    roomCode,
    hostUid: uid,
    hostNickname,
    hostRating,
    pointsToWin,
    doublingEnabled,
    betAmount,
    gameMode,
    status: "waiting",
    createdAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, PUBLIC_LOBBIES), lobbyData);
  return docRef.id;
}

/** Обновляем publicLobbies запись перед стартом — синхронизируем ставку и настройки */
export async function updatePublicLobbySettings(
  roomCode: string,
  betAmount: number,
  pointsToWin: number,
  doublingEnabled: boolean,
  gameMode?: GameMode
): Promise<void> {
  try {
    const q = query(
      collection(db, "publicLobbies"),
      where("roomCode", "==", roomCode),
      limit(1)
    );
    const snap = await getDocs(q);
    if (snap.empty) return;
    const update: Record<string, unknown> = { betAmount, pointsToWin, doublingEnabled };
    if (gameMode !== undefined) update.gameMode = gameMode;
    await setDoc(snap.docs[0].ref, update, { merge: true });
  } catch (_) {}
}

export async function closePublicLobby(lobbyId: string): Promise<void> {
  initialize();
  const docRef = doc(db, PUBLIC_LOBBIES, lobbyId);
  await deleteDoc(docRef);
}

/**
 * Полностью удаляет комнату из коллекции lobbies + publicLobbies.
 * Можно вызывать только если второй игрок ещё не зашёл.
 */
export async function deleteLobbyByCode(roomCode: string): Promise<boolean> {
  initialize();
  try {
    const q = query(
      collection(db, "lobbies"),
      where("roomCode", "==", roomCode),
      limit(5)
    );
    const snap = await getDocs(q);
    let deleted = false;
    for (const d of snap.docs) {
      const data = d.data() as FirestoreGameData;
      // Удаляем только если второй игрок не зашёл
      if (data.players?.playerTwo == null) {
        await deleteDoc(d.ref);
        deleted = true;
      }
    }
    // Удаляем и из публичного списка
    await closePublicLobbyByCode(roomCode);
    return deleted;
  } catch (err) {
    console.error("Failed to delete lobby:", err);
    return false;
  }
}

export async function closePublicLobbyByCode(roomCode: string): Promise<void> {
  initialize();
  try {
    const q = query(
      collection(db, PUBLIC_LOBBIES),
      where("roomCode", "==", roomCode),
      limit(1)
    );
    const snap = await getDocs(q);
    for (const d of snap.docs) {
      await deleteDoc(d.ref);
    }
  } catch (err) {
    console.error("Failed to close public lobby:", err);
  }
}

export function subscribeToPublicLobbies(
  callback: (lobbies: PublicLobbyData[]) => void
): () => void {
  initialize();
  // Только фильтр по status — одиночный индекс, создаётся автоматически.
  // Фильтр по времени и сортировка — клиентски, чтобы не нужен составной индекс.
  const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000;
  const q = query(
    collection(db, PUBLIC_LOBBIES),
    where("status", "==", "waiting"),
    limit(50)
  );

  const unsubscribe = onSnapshot(
    q,
    (snap) => {
      const lobbies: PublicLobbyData[] = snap.docs
        .map((d) => ({ ...(d.data() as Omit<PublicLobbyData, "id">), id: d.id }))
        .filter((l) => {
          // ?? 0 — если serverTimestamp ещё не пришёл, считаем комнату слишком старой
          const ms = (l.createdAt as any)?.toMillis?.() ?? 0;
          return ms >= thirtyMinutesAgo;
        })
        // Сортировка по убыванию
        .sort((a, b) => {
          const tA = (a.createdAt as any)?.toMillis?.() ?? 0;
          const tB = (b.createdAt as any)?.toMillis?.() ?? 0;
          return tB - tA;
        });
      callback(lobbies);
    },
    (err) => {
      console.error("subscribeToPublicLobbies error (missing index?):", err);
      callback([]);
    }
  );

  return unsubscribe;
}
